import {
  Align,
  Arrange,
  Button,
  Container,
  Icon,
  Loader,
  Spacer,
  SvgClock,
  SvgInfo,
  Text,
  TextButton,
} from '@loomhq/lens';
import pick from 'lodash/pick';
import React, { useCallback, useEffect, useRef } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useDispatch, useSelector } from 'react-redux';

import { showConfirmationModal } from 'actions/confirmationModal';
import {
  updateShareVideoInTrimMode,
  updateShareVideoRecordingVersion,
} from 'actions/shareVideo';
import { updateShowTrimExitWarning } from 'actions/trimming';
import {
  requestVideoPlayerCurrentTime,
  updateVideoPlayerEnded,
  updateVideoPlayerNotEnded,
  updateVideoPlayerSourceFromClip,
  updateVideoPlayerVideoRecordingVersion,
} from 'actions/videoPlayer';
import {
  CLIP_VIDEO_SOURCE_URL_UPDATED,
  SAVE_WEAVED_VIDEO,
  TRIM_CANCELLED,
  TRIM_CHANGE_MADE,
  TRIM_DELETE,
  TRIM_HELP_ARTICLE_CLICKED,
  TRIM_REVERT_TO_ORIGINAL,
} from 'constants/events';
import { V4, V6 } from 'constants/recordingVersions';
import { MIN_PLAY_RANGE_DURATION } from 'constants/trimming';
import { leaveEditModeCreator } from 'creators/trimming';
import {
  repositionPlayHeadInPlayRange,
  updateVideoPlayerDurationCreator,
} from 'creators/videoPlayer';
import { selectVideoId } from 'selectors/video';
import {
  selectPlayerTime,
  selectRequestedPlayerTime,
  selectVideo,
  selectVideoDuration,
  selectVideoPlayerIsFetching,
  selectVideoPlayerReadyToPlayOnSharePage,
} from 'selectors/videoPlayer';
import * as analytics from 'utilities/analytics';
import { getGraphQLClient } from 'utilities/graphql';
import { secondsToVideoTS } from 'utilities/time';
import { findCurrentPlayRangeIdx } from 'utilities/trimming';

import ClipEditor from './clip-editor';
import TrimControlBar from './control-bar';
import { useClipsState } from './clips/state';
import { DeleteDraftClips } from './graphql/DeleteDraftClips.gql';
import { PrepareForEdit } from './graphql/PrepareForEdit.gql';
import { RevertToOriginal } from './graphql/RevertToOriginal.gql';
import { UpdateVideoClips } from './graphql/UpdateVideoClips.gql';
import { Video } from '../../../../../shared/types/clips';

import styles from './styles.module.less';

const MIN_CLIP_FRACTION_SIZE = 0.05;

const updateVideoClips = async (videoId, clips) => {
  const gqlClient = getGraphQLClient();

  const clipsData = clips.map(clip => ({
    id: clip.id,
    position: clip.position,
    trim_ranges: clip.trimRanges.map(range => pick(range, ['from', 'to'])),
  }));

  const { data } = await gqlClient.mutate({
    mutation: UpdateVideoClips,
    variables: {
      videoId,
      clips: clipsData,
    },
    fetchPolicy: 'no-cache',
  });

  return data;
};

// TODO fix for weave
const findClosestPlayRangeAndLoc = (time, playRanges) => {
  let playRangeIdx: number;
  let closestLoc: number;
  let distance = Infinity;

  for (let idx = 0; idx < playRanges.length; idx++) {
    const range = playRanges[idx];

    if (Math.abs(range.from - time) < distance) {
      closestLoc = range.from;
      playRangeIdx = idx;
      distance = Math.abs(range.from - time);
    }

    if (Math.abs(range.to - time) < distance) {
      closestLoc = range.to;
      playRangeIdx = idx;
      distance = Math.abs(range.to - time);
    }
  }

  // @ts-expect-error ignore due to enabling strict null checks
  return { closestLoc, playRangeIdx };
};

const EditTools = (): JSX.Element => {
  const {
    clips,
    addClips,
    undo,
    redo,
    removeClip,
    focusedPlayRangeIndex,
    removePlayRangeFromClip,
    hasBeenUpdated,
    resetState,
    focusedClipIndex,
    updateFocusedClipIndex,
    updateCurrentClipDuration,
    updateFocusedPlayRangeIndex,
    canUndo,
    canRedo,
    canDeletePlayRange,
    hasTrims,
    currentClip,
    // currentPlayRange,
    playRanges,
    trimVideoDuration,
    duration,
    splitCurrentPlayRange,
    setClips,
    recentlyAddedInfo,
  } = useClipsState(state => ({
    addClips: state.addClips,
    clips: state.clips,
    focusedClipIndex: state.focusedClipIndex,
    focusedPlayRangeIndex: state.focusedPlayRangeIndex,
    hasBeenUpdated: state.hasBeenUpdated,
    removeClip: state.removeClip,
    removePlayRangeFromClip: state.removePlayRangeFromClip,
    resetState: state.resetState,
    updateFocusedClipIndex: state.updateFocusedClipIndex,
    undo: state.undo,
    redo: state.redo,
    updateCurrentClipDuration: state.updateCurrentClipDuration,
    updateFocusedPlayRangeIndex: state.updateFocusedPlayRangeIndex,
    canUndo: state.canUndo,
    canRedo: state.canRedo,
    canDeletePlayRange: state.canDeletePlayRange,
    getDuration: state.getDuration,
    hasTrims: state.hasTrims,
    trimVideoDuration: state.getTrimVideoDuration(),
    currentClip: state.getCurrentClip(),
    currentPlayRange: state.getCurrentPlayRanges()[state.focusedPlayRangeIndex],
    playRanges: state.getCurrentPlayRanges(),
    duration: state.getDuration(),
    splitCurrentPlayRange: state.splitCurrentPlayRange,
    setClips: state.setClips,
    recentlyAddedInfo: state.recentlyAddedInfo,
  }));

  const dispatch = useDispatch();
  const currentTime: number = useSelector(selectPlayerTime);
  const requestedCurrentTime = useSelector(selectRequestedPlayerTime);
  const videoDuration: number = useSelector(selectVideoDuration);
  const video: Video = useSelector(selectVideo);
  const videoId: string = useSelector(selectVideoId);

  // TODO fix/eliminate all following for weave
  const isFetching = useSelector(selectVideoPlayerIsFetching);
  const readyToPlay = useSelector(selectVideoPlayerReadyToPlayOnSharePage);

  const selectedPlayRange = playRanges[focusedPlayRangeIndex] ?? null;
  const currentTimeInSelectedPlayRangeBounds = Boolean(
    selectedPlayRange &&
      currentTime - selectedPlayRange.from >= MIN_PLAY_RANGE_DURATION &&
      selectedPlayRange.to - currentTime >= MIN_PLAY_RANGE_DURATION
  );
  const canSplitPlayRange = Boolean(
    playRanges.length > 0 &&
      requestedCurrentTime == null &&
      currentTimeInSelectedPlayRangeBounds
  );

  // This effect is here b/c we do not have an accurate duration until the
  // video loads. The duration in video_player is the duration of the
  // trimmed video and the duration in video_player.video.video_properties
  // is rounded which can mess up our play range values. We will only update
  // the current clip duration if there is a change in the duration stored in
  // redux compared to the clips current duration is within 1 second.
  //
  // The updateDuration function is stored in a ref so we can update it only
  // once the focusedClipIndex has changed. Without that, it would be called
  // with the wrong focusedClipIndex and duration.
  const updateDurationFnRef = useRef<(videoDuration: number) => void>();

  useEffect(() => {
    updateDurationFnRef.current = videoDuration => {
      const currentDuration = currentClip?.duration || 0;
      const videoCurrentDurationDifference = Math.abs(
        videoDuration - currentDuration
      );

      if (
        clips.length > 0 &&
        focusedClipIndex != null &&
        Number(videoDuration) > 0 &&
        (currentDuration === 0 ||
          currentDuration == null ||
          (videoCurrentDurationDifference > 0 &&
            videoCurrentDurationDifference < 1))
      ) {
        updateCurrentClipDuration(videoDuration);
      }
    };
  }, [clips.length, focusedClipIndex, updateCurrentClipDuration, currentClip]);

  useEffect(() => {
    if (updateDurationFnRef.current) {
      updateDurationFnRef.current(videoDuration);
    }
  }, [videoDuration]);

  const trimButtonsRef = useCallback(buttonsNode => {
    if (buttonsNode) {
      buttonsNode.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }
  }, []);

  useHotkeys(
    'cmd+z,ctrl+z',
    e => {
      e.preventDefault();
      undo(videoId);
    },
    { enabled: canUndo() }
  );

  useHotkeys(
    'cmd+shift+z,ctrl+shift+z',
    e => {
      e.preventDefault();
      redo(videoId);
    },
    {
      enabled: canRedo(),
    }
  );

  // Handle URL changes, back button, page reload with unsaved changes
  const unloadEventListenerRef = useRef(() => '');

  useEffect(() => {
    const removeEventListener = () => {
      if (unloadEventListenerRef.current) {
        window.removeEventListener(
          'beforeunload',
          unloadEventListenerRef.current,
          { capture: true }
        );
      }
    };

    const showWarningModal = event => {
      event.preventDefault();

      return (event.returnValue =
        'There are unsaved changes to your video. Are you sure you want to discard them?');
    };

    removeEventListener();

    if (hasBeenUpdated) {
      // @ts-expect-error ignore due to enabling strict null checks
      unloadEventListenerRef.current = showWarningModal;
      window.addEventListener('beforeunload', unloadEventListenerRef.current, {
        capture: true,
      });
    }

    return removeEventListener;
  }, [hasBeenUpdated, dispatch]);

  const prepareForEditing = useCallback(
    async (videoId, addClips) => {
      const gqlClient = getGraphQLClient();

      const { data } = await gqlClient.mutate({
        mutation: PrepareForEdit,
        variables: {
          videoId,
        },
        fetchPolicy: 'no-cache',
      });

      if (data?.prepareForEdit?.message == null) {
        await addClips(data.prepareForEdit.video.clips, { initial: true });

        const recordingVersion =
          data?.prepareForEdit?.video?.video_properties?.recording_version;

        const firstClip = data.prepareForEdit.video.clips.find(
          clip => clip?.position === 1 && clip?.source_url
        );

        if (firstClip) {
          dispatch(
            updateVideoPlayerSourceFromClip({
              sourceUrl: firstClip?.source_url,
              sourceMimeType: firstClip?.source_mime_type,
              partCredentials: firstClip?.source_part_credentials,
              sourceUrlIsTranscoded: firstClip?.source_url_is_transcoded,
              seekTime: 0,
            })
          );

          analytics.track(CLIP_VIDEO_SOURCE_URL_UPDATED, {
            id: videoId,
            clipId: firstClip?.id,
            event_type: 'on video load',
          });
        }

        dispatch(updateVideoPlayerVideoRecordingVersion(recordingVersion));
        dispatch(updateShareVideoRecordingVersion(recordingVersion));
      } else {
        // TODO: handle error when no clips
      }
    },
    [dispatch]
  );

  useEffect(() => {
    // V4 video (uploads) are not supported in weave at this time
    if (video.video_properties.recording_version !== V4) {
      prepareForEditing(videoId, addClips);
    }
  }, [
    videoId,
    addClips,
    video.video_properties.recording_version,
    prepareForEditing,
  ]);

  const updateSelectedPlayRange = () => {
    if (clips.length === 0) {
      return;
    }

    if (requestedCurrentTime != null) {
      return;
    }

    const selectedPlayRange = playRanges[focusedPlayRangeIndex];

    if (
      !selectedPlayRange ||
      currentTime < selectedPlayRange.from ||
      currentTime > selectedPlayRange.to
    ) {
      const idx = findCurrentPlayRangeIdx(currentTime, playRanges);

      if (idx !== -1) {
        updateFocusedPlayRangeIndex(idx);
      }
    }
  };

  useEffect(updateSelectedPlayRange, [
    currentTime,
    clips.length,
    playRanges,
    focusedPlayRangeIndex,
    requestedCurrentTime,
    updateFocusedPlayRangeIndex,
  ]);

  /* keep the playhead inside the play ranges */
  const placePlayheadInPlayRanges = () => {
    if (clips.length === 0) {
      return;
    }

    // If we are in a play range already or time is being updated
    if (requestedCurrentTime != null) {
      return;
    }

    if (playRanges.some(r => currentTime >= r.from && currentTime <= r.to)) {
      // the current time is already within a play range
      return;
    }

    // If we are not in a play range, move to the closest one
    const { closestLoc, playRangeIdx } = findClosestPlayRangeAndLoc(
      currentTime,
      playRanges
    );

    // if we're still be in the same play range
    if (playRangeIdx === focusedPlayRangeIndex) {
      return;
    }

    const lastPlayRangeTo = playRanges[playRanges.length - 1]?.to;

    if (lastPlayRangeTo === closestLoc) {
      // other code is already going to swap clips
      return;
    }

    dispatch(
      repositionPlayHeadInPlayRange({
        newPlayerTime: closestLoc,
        updateSelectedPlayRange: () =>
          updateFocusedPlayRangeIndex(playRangeIdx),
      })
    );
  };

  useEffect(placePlayheadInPlayRanges, [
    dispatch,
    clips.length,
    playRanges,
    currentTime,
    focusedPlayRangeIndex,
    updateFocusedPlayRangeIndex,
    requestedCurrentTime,
  ]);

  // commented out this piece of code as this does not let a user
  // click into a specific timestamp within a play range.
  // update playhead when focused clip or play range changes
  // useEffect(() => {
  //   if (currentPlayRange) {
  //     dispatch(requestVideoPlayerCurrentTime(currentPlayRange.from));
  //   }
  // }, [dispatch, clips?.length, currentClip?.id, currentPlayRange?.from]);

  useEffect(() => {
    // If we are at the end of the last play range
    if (!currentClip || !currentClip.playRanges) {
      return;
    }

    const lastPlayRangeTo =
      currentClip.playRanges[currentClip.playRanges.length - 1]?.to;

    // if we trim the end of a clip then when the last
    // play range of a clip finishes then the playhead's current
    // time goes to the end time stamp of the video which is
    // greater than the last playranges' to
    if (currentTime >= lastPlayRangeTo && requestedCurrentTime == null) {
      // only dispatch at last clip playrange
      if (focusedClipIndex === clips.length - 1) {
        dispatch(updateVideoPlayerEnded());
      } else {
        dispatch(updateVideoPlayerNotEnded());
      }
    } else {
      dispatch(updateVideoPlayerNotEnded());
    }
  }, [
    currentTime,
    requestedCurrentTime,
    playRanges,
    clips,
    currentClip,
    focusedClipIndex,
    updateFocusedClipIndex,
    dispatch,
    videoId,
  ]);

  const deletePlayRange = () => {
    analytics.track(TRIM_DELETE, {
      id: videoId,
      has_been_trimmed: true,
      event_type: 'click',
    });

    if (!playRanges) {
      return;
    }

    if (playRanges.length <= 1) {
      // last play range in the clip, we should just remove the clip itself
      removeClip(focusedClipIndex);
    } else {
      removePlayRangeFromClip(focusedClipIndex, focusedPlayRangeIndex);
    }
  };

  const splitPlayRange = () => {
    splitCurrentPlayRange(currentTime);

    analytics.track(TRIM_CHANGE_MADE, {
      id: videoId,
      type: 'split',
      hotkey: false,
      has_been_trimmed: hasTrims,
      event_type: 'click',
    });
  };

  const saveChanges = async () => {
    analytics.track(SAVE_WEAVED_VIDEO, {
      videoId: video.id,
      clipsCount: clips.length,
      duration,
    });

    const response = await updateVideoClips(videoId, clips);
    const patchedVideo = response?.updateVideoClips?.video;

    dispatch(leaveEditModeCreator(patchedVideo));
    dispatch(updateVideoPlayerDurationCreator(duration));
    dispatch(updateShareVideoInTrimMode(false));
    dispatch(showConfirmationModal('Your changes have been saved.'));
    resetState();
  };

  const exitWithoutSaving = () => {
    if (hasBeenUpdated) {
      dispatch(updateShowTrimExitWarning(true));
    } else {
      goBackToVideo();
    }
  };

  const goBackToVideo = async () => {
    dispatch(leaveEditModeCreator());

    const gqlClient = getGraphQLClient();

    await gqlClient.mutate({
      mutation: DeleteDraftClips,
      variables: {
        rootVideoId: videoId,
      },
      fetchPolicy: 'no-cache',
    });

    analytics.track(TRIM_CANCELLED, {
      id: videoId,
      has_been_trimmed: hasTrims,
      event_type: 'click',
    });
  };

  const revertToOriginal = async () => {
    analytics.track(TRIM_REVERT_TO_ORIGINAL, {
      id: videoId,
      has_been_trimmed: hasTrims,
      event_type: 'click',
    });
    const gqlClient = getGraphQLClient();

    const { data } = await gqlClient.mutate({
      mutation: RevertToOriginal,
      variables: {
        videoId,
      },
      fetchPolicy: 'no-cache',
    });

    if (
      data.revertToOriginal.__typename === 'RevertToOriginalPayload' &&
      data.revertToOriginal.video.clips.length !== 0
    ) {
      setClips(data.revertToOriginal.video.clips);
    }
  };

  const trimControlBarProps = {
    currentTime,
    canSplitPlayRange,
    splitPlayRange,
    canDeletePlayRange: canDeletePlayRange(),
    deletePlayRange,
    canUndo: canUndo(),
    canRedo: canRedo(),
    undo: () => undo(videoId),
    redo: () => redo(videoId),
    revertToOriginal,
    canRevertToOriginal: true,
    videoId,
  };

  const isReady =
    !isFetching &&
    /* readyToPlay OR if you are V6 we are going to temporarily
    load the page anyway instead of showing a loading spinner */
    (readyToPlay || video?.video_properties?.recording_version === V6);

  if (!isReady) {
    return (
      <Container paddingTop="xlarge">
        <Align alignment="center">
          <Loader size="large" />
        </Align>
      </Container>
    );
  }

  const durationChanged = duration != trimVideoDuration;

  const allClipsDuration = clips.reduce(
    (acc, c) => acc + c.video_properties.duration,
    0
  );

  const handleClick = (
    event: React.MouseEvent,
    { secs, clipIdx }: { secs: number; clipIdx: number }
  ) => {
    if (clipIdx !== -1 && clipIdx < clips.length) {
      const clip = clips[clipIdx];

      for (const [rangeIdx, { from, to }] of clip.playRanges?.entries() || []) {
        if (secs >= from && secs <= to) {
          updateFocusedPlayRangeIndex(rangeIdx);
          dispatch(requestVideoPlayerCurrentTime(secs));
        }
      }
    }
  };

  return (
    <Arrange justifyContent="stretch" autoFlow="row" id="edit-tools">
      <Spacer top="large" />
      <TrimControlBar {...trimControlBarProps} />
      <Spacer top="small" />
      <Arrange
        gap="large"
        columns={clips.map(
          clip =>
            `${Math.max(
              clip.video_properties.duration / allClipsDuration,
              MIN_CLIP_FRACTION_SIZE
            )}fr`
        )}
      >
        {clips.map((clip, idx) => (
          <ClipEditor
            key={clip.id}
            clip={clip}
            idx={idx}
            onClick={handleClick}
            currentTime={
              typeof requestedCurrentTime === 'number'
                ? requestedCurrentTime
                : currentTime
            }
            recentlyAddedInfo={
              recentlyAddedInfo?.clipIds[0] == clip.id && (
                <div>
                  {recentlyAddedInfo.clipIds.length}{' '}
                  {recentlyAddedInfo.clipIds.length == 1 ? 'clip' : 'clips'}{' '}
                  added from <strong>{recentlyAddedInfo.name}</strong>
                </div>
              )
            }
          />
        ))}
      </Arrange>
      <div ref={trimButtonsRef}>
        <Align alignment="center">
          <div className={styles.durationChange}>
            {durationChanged ? (
              <Arrange gap="xsmall">
                <Icon color="bodyDimmed" icon={<SvgClock />} />{' '}
                <Text
                  size="medium"
                  color="bodyDimmed"
                  fontWeight="medium"
                  className={styles.timeDisplay}
                >
                  {secondsToVideoTS(duration, 1)}
                </Text>{' '}
                <Text size="medium" color="bodyDimmed" fontWeight="medium">
                  &rarr;
                </Text>
                <Text
                  size="medium"
                  color="body"
                  fontWeight="medium"
                  className={styles.timeDisplay}
                >
                  {secondsToVideoTS(trimVideoDuration, 1)}
                </Text>
              </Arrange>
            ) : (
              <Text size="medium">
                Trim your video by dragging the handles and using the Split
                button.
              </Text>
            )}
          </div>

          <TextButton
            size="small"
            icon={<Icon size={2} icon={<SvgInfo />} color="primary" />}
            htmlTag="a"
            // @ts-expect-error removed lens.d.ts
            target="_blank"
            href="https://support.loom.com/hc/en-us/articles/360002217478-How-to-trim-your-Loom-video-"
            onClick={() =>
              analytics.track(TRIM_HELP_ARTICLE_CLICKED, {
                id: videoId,
                has_been_trimmed: hasTrims,
                event_type: 'click',
              })
            }
          >
            <Text color="primary" size="small" fontWeight="medium">
              View help article
            </Text>
          </TextButton>

          <Spacer top="medium" />
          <Arrange gap="small">
            <Button onClick={exitWithoutSaving}>Cancel</Button>
            <Button
              variant="primary"
              isDisabled={!hasBeenUpdated}
              onClick={saveChanges}
            >
              Save changes
            </Button>
          </Arrange>
        </Align>
      </div>
    </Arrange>
  );
};

export default EditTools;
